
<script setup lang="ts">
import { ref, computed } from 'vue'
import { useAuthFirebase } from '~/layers/auth/composables/useAuthFirebase'
import { 
  LayoutDashboard,
  FileText,
  FilePenLine,
  Users, 
  Settings,
  Send,
} from 'lucide-vue-next'

const { authUser, logout } = useAuthFirebase()

const isSidebarOpen = ref(false)
const showQuickActions = ref(false)
const notificationCount = ref(3) // This would come from your API

// Navigation items for the sidebar
const navigationItems = ref([
  {
    label: 'Dashboard',
    path: '/dashboard',
    icon: LayoutDashboard
  },
  {
    label: 'Documents',
    path: '/documents',
    icon: FileText
  },
  {
    label: 'Draft Documents',
    path: '/document-drafts',
    icon: FilePenLine
  },
  {
    label: 'Send Document',
    path: '/send',
    icon: Send
  },
  {
    label: 'Settings',
    path: '/settings',
    icon: Settings
  }
])

// Computed properties for user info
const userName = computed(() => {
  if (authUser.value) {
    return `${authUser.value.first_name} ${authUser.value.last_name}`
  }
  return 'User'
})

const userEmail = computed(() => {
  return authUser.value?.email || 'user@example.com'
})

const pageTitle = computed(() => {
  const route = useRoute()
  const path = route.path
  
  if (path === '/dashboard') return 'Dashboard'
  if (path.startsWith('/documents')) return 'Documents'
  if (path.startsWith('/document-drafts')) return 'Draft Documents'
  if (path.startsWith('/document/')) return 'Document Details'
  if (path.startsWith('/templates')) return 'Templates'
  if (path.startsWith('/team')) return 'Team'
  if (path.startsWith('/settings')) return 'Settings'
  if (path.startsWith('/send')) return 'Send Document'
  
  return 'Dashboard'
})

// Logout confirmation modal state
const showLogoutConfirmation = ref(false)
const isLoggingOut = ref(false)

const handleLogoutClick = () => {
  showLogoutConfirmation.value = true
}

const handleLogoutConfirm = async () => {
  isLoggingOut.value = true
  try {
    await logout()
  } catch (error) {
    console.error('Logout failed:', error)
  } finally {
    isLoggingOut.value = false
    showLogoutConfirmation.value = false
  }
}

const handleLogoutCancel = () => {
  showLogoutConfirmation.value = false
}

// Close sidebar when route changes
watch(() => useRoute().path, () => {
  isSidebarOpen.value = false
})
</script>
<template>
  <div class="min-h-screen bg-brand-bg">
    <!-- BaseSidebar Component - Fixed Position -->
    <BaseSidebar
      v-model:is-open="isSidebarOpen"
      title="zC-Sign"
      :navigation-items="navigationItems"
      :show-user-profile="true"
      :user-name="userName"
      :user-email="userEmail"
      @logout="handleLogoutClick"
    />

    <!-- Main Content Area - With left margin to account for fixed sidebar -->
    <div class="lg:ml-64 flex flex-col min-h-screen">
      <!-- Top Navigation -->
      <div class="sticky top-0 z-30 bg-white border-b border-gray-300">
        <div class="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
          <!-- Mobile menu button -->
          <button 
            @click="isSidebarOpen = true"
            class="lg:hidden text-emerald-600 hover:text-emerald-700"
          >
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>

          <!-- Page Title -->
          <div class="flex-1 lg:flex-none">
            <h2 class="text-lg font-semibold text-brand-blue">{{ pageTitle }}</h2>
          </div>

          <!-- Right side actions -->
          <div class="flex items-center space-x-4">
            <!-- Notifications -->
            <!-- <button class="text-brand-blue hover:text-blue-700 relative">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-5 5v-5z" />
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
              <span v-if="notificationCount > 0" class="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                {{ notificationCount }}
              </span>
            </button> -->

            <!-- Quick Actions -->
            <!-- <BaseButton
              @click="showQuickActions = !showQuickActions"
              class="flex items-center bg-brand-blue hover:bg-blue-700 text-white border-none px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200"
            >
              <svg class="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
              </svg>
              <span>New Document</span>
            </BaseButton> -->
          </div>
        </div>
      </div>

      <!-- Page Content -->
      <main class="flex-1 p-4 sm:p-6 lg:p-8 overflow-auto">
        <slot />
      </main>
    </div>

    <!-- Quick Actions Dropdown -->
    <div v-if="showQuickActions" class="fixed inset-0 z-40" @click="showQuickActions = false">
      <div class="absolute top-20 right-4 bg-white rounded-lg shadow-lg border border-emerald-100 py-2 w-48">
        <NuxtLink 
          to="/documents/new" 
          class="flex items-center px-4 py-2 text-sm text-emerald-700 hover:bg-emerald-50"
          @click="showQuickActions = false"
        >
          <svg class="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          Upload Document
        </NuxtLink>
        <NuxtLink 
          to="/templates/new" 
          class="flex items-center px-4 py-2 text-sm text-emerald-700 hover:bg-emerald-50"
          @click="showQuickActions = false"
        >
          <svg class="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
          </svg>
          Create Template
        </NuxtLink>
        <NuxtLink 
          to="/team/invite" 
          class="flex items-center px-4 py-2 text-sm text-emerald-700 hover:bg-emerald-50"
          @click="showQuickActions = false"
        >
          <svg class="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
          </svg>
          Invite Team Member
        </NuxtLink>
      </div>
    </div>

    <!-- Logout Confirmation Modal -->
    <BaseConfirmationModal
      v-model:open="showLogoutConfirmation"
      title="Confirm Logout"
      message="Are you sure you want to logout? You will need to sign in again to access your account."
      confirm-text="Logout"
      cancel-text="Cancel"
      type="warning"
      :is-loading="isLoggingOut"
      @confirm="handleLogoutConfirm"
      @cancel="handleLogoutCancel"
    />
  </div>
</template>
<style scoped>
/* Smooth transitions */
.transition-transform {
  transition-property: transform;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 300ms;
}
</style> 